import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  useAssignedTasks,
  useMemberStats,
  useCreateTask,
  useSystemSettings,
} from '../hooks/api';
import { SYSTEM_SETTINGS_DEFAULTS } from '../constants/systemSettingsDefaults';
import {
  Clock,
  Square,
  Zap,
  ChevronUp,
  CheckSquare,
  Calendar,
  Plus,
  Target,
  Loader2,
  X
} from 'lucide-react';
import { useTimer } from '../contexts/TimerContext';
import { useAuth } from '../contexts/AuthContext';
import LeaveModal from './LeaveModal';
import TaskFormDrawer from './TaskFormDrawer';
import { toast } from 'react-hot-toast';

// Utility function to format time
const formatTime = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Shell: skip mounting data hooks for admins (Quick Access is hidden for them).
 */
const QuickAccess = () => {
  const { user } = useAuth();
  if (user?.role === 'admin') {
    return null;
  }
  return <QuickAccessPanel />;
};

/**
 * Panel body — hooks only mount for non-admin users.
 */
const QuickAccessPanel = () => {
  const [quickAccessExpanded, setQuickAccessExpanded] = useState(true);
  const [stoppingTimer, setStoppingTimer] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showTimerStopModal, setShowTimerStopModal] = useState(false);
  const [timerStopDescription, setTimerStopDescription] = useState('');
  const [timerStopLoading, setTimerStopLoading] = useState(false);
  const { activeTimer, stopTimer, elapsedTime } = useTimer();
  const { user } = useAuth();
  const { data: systemSettings } = useSystemSettings();
  const dailyHoursCap = systemSettings?.daily_hours_cap ?? SYSTEM_SETTINGS_DEFAULTS.daily_hours_cap;
  const minDescLen = systemSettings?.min_description_length ?? SYSTEM_SETTINGS_DEFAULTS.min_description_length;
  const navigate = useNavigate();

  const {
    data: assignedTasksData,
    isLoading: assignedTasksLoading
  } = useAssignedTasks();

  const {
    data: memberStatsData,
    isLoading: memberStatsLoading
  } = useMemberStats('today');

  const createTaskMutation = useCreateTask();

  const allTasks = assignedTasksData?.results || [];
  const memberStats = memberStatsData;

  const activeTasks = useMemo(() => {
    if (!allTasks.length || !user?.id) return [];
    return allTasks.filter(task =>
      !task.is_deleted &&
      Array.isArray(task.assigned_to) &&
      task.assigned_to.includes(user.id) &&
      task.status !== 'Done'
    );
  }, [allTasks, user?.id]);

  const loading = assignedTasksLoading || memberStatsLoading;

  const checkDailyHoursLimitForTimer = async () => {
    try {
      const response = await axios.get('/api/time-entries/daily-hours');
      const totalHoursToday = response.data.total_hours;

      if (!activeTimer) {
        return true;
      }

      const timerStartTime = new Date(activeTimer.start_time);
      const now = new Date();
      const timerDurationMs = now - timerStartTime;
      const currentTimerHours = timerDurationMs / (1000 * 60 * 60);

      const totalAfterTimer = totalHoursToday + currentTimerHours;

      if (totalAfterTimer > dailyHoursCap) {
        const remainingHours = Math.max(0, dailyHoursCap - totalHoursToday);
        toast.warning(
          `Timer will be capped at ${remainingHours.toFixed(1)}h (saved) to stay within the daily limit of ${dailyHoursCap}h. You have tracked ${totalHoursToday.toFixed(1)} hours today.`
        );
      }

      return true;
    } catch (error) {
      console.error('Error checking daily hours for timer:', error);
      return true;
    }
  };

  const handleConfirmStopTimer = async () => {
    if (!timerStopDescription.trim()) {
      toast.error('Please provide a description for the timer session');
      return;
    }

    if (timerStopDescription.trim().length < minDescLen) {
      toast.error(`Description must be at least ${minDescLen} characters long`);
      return;
    }

    const canStopTimer = await checkDailyHoursLimitForTimer();
    if (!canStopTimer) {
      return;
    }

    setTimerStopLoading(true);
    setStoppingTimer(true);
    try {
      await stopTimer(timerStopDescription.trim());
      setShowTimerStopModal(false);
      setTimerStopDescription('');
      toast.success('Timer stopped successfully');
    } catch (error) {
      console.error('Error stopping timer:', error);
      toast.error('Failed to stop timer');
    } finally {
      setTimerStopLoading(false);
      setStoppingTimer(false);
    }
  };

  const handleViewTask = () => {
    if (activeTimer?.task_id) {
      navigate(`/tasks/${activeTimer.task_id}`);
    } else {
      navigate('/time-entries');
    }
  };

  const handleCreateTaskSubmit = async (taskData) => {
    const payload = {
      ...taskData,
      due_date: taskData.due_date ? new Date(taskData.due_date).toISOString() : null,
    };
    await createTaskMutation.mutateAsync(payload);
    toast.success('Task created successfully');
  };

  return (
    <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-40 pb-[env(safe-area-inset-bottom,0px)] pr-[env(safe-area-inset-right,0px)]">
      {quickAccessExpanded ? (
        <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-lg border border-secondary-200 dark:border-secondary-700 w-[min(15rem,calc(100vw-2rem))] sm:w-64 p-3">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-secondary-900 dark:text-white">Quick Access</h3>
            <button
              onClick={() => setQuickAccessExpanded(false)}
              className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-secondary-100 dark:hover:bg-secondary-700 rounded transition-colors"
              aria-label="Collapse quick access"
            >
              <ChevronUp className="h-4 w-4 text-secondary-500" />
            </button>
          </div>

          <div className="mb-3 p-2 bg-secondary-50 rounded-lg">
            <div className="text-xs font-medium text-secondary-700 mb-1">Today's Stats</div>
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3 text-primary-600" />
                <span className="text-secondary-600">
                  {loading ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    `${memberStats?.periodHours ? memberStats.periodHours.toFixed(1) : '0'}h`
                  )}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Target className="h-3 w-3 text-green-600" />
                <span className="text-secondary-600">
                  {loading ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    `${activeTasks.length} active`
                  )}
                </span>
              </div>
            </div>
          </div>

          <div className="mb-3">
            <div className="text-xs font-medium text-secondary-700 mb-2">Quick Actions</div>
            <div className="grid grid-cols-2 gap-1.5">
              <button
                onClick={() => navigate('/time-entries')}
                className="flex items-center gap-1 bg-primary-50 hover:bg-primary-100 text-primary-700 text-xs py-1.5 px-2 rounded transition-colors"
              >
                <Clock className="h-3 w-3" />
                Log Time
              </button>
              <button
                onClick={() => navigate('/tasks')}
                className="flex items-center gap-1 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs py-1.5 px-2 rounded transition-colors"
              >
                <CheckSquare className="h-3 w-3" />
                View Tasks
              </button>
              <button
                onClick={() => setShowLeaveModal(true)}
                className="flex items-center gap-1 bg-green-50 hover:bg-green-100 text-green-700 text-xs py-1.5 px-2 rounded transition-colors"
              >
                <Calendar className="h-3 w-3" />
                Request Leave
              </button>
              {user?.role === 'project_lead' ? (
                <button
                  onClick={() => setShowTaskModal(true)}
                  className="flex items-center gap-1 bg-purple-50 hover:bg-purple-100 text-purple-700 text-xs py-1.5 px-2 rounded transition-colors"
                >
                  <Plus className="h-3 w-3" />
                  Create Task
                </button>
              ) : (
                <button
                  onClick={() => navigate('/projects')}
                  className="flex items-center gap-1 bg-purple-50 hover:bg-purple-100 text-purple-700 text-xs py-1.5 px-2 rounded transition-colors"
                >
                  <CheckSquare className="h-3 w-3" />
                  View Projects
                </button>
              )}
            </div>
          </div>

          {activeTimer && (
            <div className="mt-2 pt-2 border-t border-secondary-100">
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-1.5">
                  {stoppingTimer ? (
                    <>
                      <Loader2 className="w-1.5 h-1.5 text-orange-500 animate-spin" />
                      <span className="text-xs font-medium text-orange-600">Stopping Timer...</span>
                    </>
                  ) : (
                    <>
                      <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></div>
                      <span className="text-xs font-medium text-red-600">Timer Active</span>
                    </>
                  )}
                </div>
                <div className={`text-xs font-mono font-bold ${stoppingTimer ? 'text-orange-600' : 'text-red-600'}`}>
                  {formatTime(elapsedTime)}
                </div>
              </div>
              <div className="mb-1.5">
                <div className="text-xs text-secondary-600 truncate" title={activeTimer.description}>
                  {activeTimer.description}
                </div>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => setShowTimerStopModal(true)}
                  disabled={stoppingTimer}
                  className={`flex-1 text-white text-xs py-1.5 px-2 rounded transition-colors font-medium flex items-center justify-center gap-1 ${
                    stoppingTimer
                      ? 'bg-red-500 cursor-not-allowed'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {stoppingTimer ? (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Stopping...
                    </>
                  ) : (
                    <>
                      <Square className="h-3 w-3" />
                      Stop
                    </>
                  )}
                </button>
                <button
                  onClick={handleViewTask}
                  className="flex-1 bg-secondary-100 hover:bg-secondary-200 text-secondary-700 text-xs py-1.5 px-2 rounded transition-colors font-medium flex items-center justify-center gap-1"
                >
                  <CheckSquare className="h-3 w-3" />
                  View
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="relative">
          <button
            onClick={() => setQuickAccessExpanded(true)}
            aria-label="Open quick access"
            className={`p-3 min-h-[48px] min-w-[48px] flex items-center justify-center rounded-full shadow-lg transition-colors ${
              stoppingTimer
                ? 'bg-orange-600 cursor-not-allowed'
                : activeTimer
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-primary-600 hover:bg-primary-700'
            } text-white`}
          >
            {stoppingTimer ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : activeTimer ? (
              <div className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
                <Clock className="h-4 w-4" />
              </div>
            ) : (
              <Zap className="h-4 w-4" />
            )}
          </button>

          {activeTimer && !stoppingTimer && (
            <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
              {Math.floor(elapsedTime / 60)}
            </div>
          )}
          {stoppingTimer && (
            <div className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
              ...
            </div>
          )}
        </div>
      )}

      <LeaveModal
        isOpen={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        onSuccess={() => {}}
      />

      {user?.role === 'project_lead' && (
        <TaskFormDrawer
          open={showTaskModal}
          onClose={() => setShowTaskModal(false)}
          editingTask={null}
          currentUser={user}
          onSubmit={handleCreateTaskSubmit}
        />
      )}

      {showTimerStopModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setShowTimerStopModal(false)}>
          <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-2xl w-full max-w-md mx-4 transform transition-all duration-300 scale-100 animate-in fade-in-0 zoom-in-95" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 pb-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Square className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Stop Timer</h3>
                  <p className="text-sm text-gray-500">Add a description for your work session</p>
                </div>
              </div>
              <button
                onClick={() => setShowTimerStopModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 pt-4">
              <div className="space-y-5">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Session Duration:</span>
                    <span className="text-lg font-semibold text-gray-900">
                      {activeTimer && formatTime(elapsedTime)}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description <span className="text-red-500 text-xs font-normal">*</span>
                    <span className="text-xs text-gray-500 font-normal ml-1">(min. {minDescLen} characters)</span>
                  </label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none transition-all duration-200"
                    placeholder="What did you work on during this session? Please provide detailed description of your work..."
                    rows={3}
                    value={timerStopDescription}
                    onChange={e => setTimerStopDescription(e.target.value)}
                    required
                  />
                  <div className="flex justify-between items-center mt-1">
                    <span className={`text-xs font-medium ${timerStopDescription.length < minDescLen ? 'text-red-500' : 'text-green-600'}`}>
                      {timerStopDescription.length}/{minDescLen}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-100 mt-6">
                <button
                  type="button"
                  onClick={() => setShowTimerStopModal(false)}
                  className="flex-1 px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmStopTimer}
                  className="flex-1 px-4 py-3 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg flex items-center justify-center gap-2 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={timerStopLoading || !timerStopDescription.trim() || timerStopDescription.trim().length < minDescLen}
                >
                  {timerStopLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Stopping...
                    </>
                  ) : (
                    <>
                      <Square className="h-4 w-4" />
                      Stop Timer
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickAccess;
